import { useState } from "react";
import { CaretDown } from "@phosphor-icons/react";

/**
 * FAQAccordion — visible HTML FAQ section.
 * Mirrors the 12 questions in /public/index.html JSON-LD so search-engine
 * crawlers see consistent Q&A in both AI Overview JSON-LD AND the rendered
 * HTML body — the dual-source requirement Google's 2026 helpful-content
 * guidance asks for.
 */
export const FAQ_ITEMS = [
  {
    q: "Should I rent or buy a flat in India in 2026?",
    a: "For most Indian metros in 2026, the rent-vs-buy break-even is 6–9 years assuming 20% down payment, 8.75% home loan, 7% property appreciation and 5% rent escalation. If you'll stay under 5 years, rent. If you'll stay 7+ years and earn at least ₹1.5L/month net, buying makes financial sense. Use Estima's Rent vs Buy calculator for your exact city and EMI.",
  },
  {
    q: "What is the EMI for a ₹50 lakh home loan at 8.75% over 20 years?",
    a: "₹50 lakh at 8.75% for 240 months is approximately ₹44,186 per month using standard amortisation. Total interest paid over the tenure ≈ ₹56 lakh; total outflow ≈ ₹1.06 crore. Estima's EMI calculator supports any principal, tenure, rate combination plus step-up EMI plans and prepayment scenarios.",
  },
  {
    q: "What is the lowest home loan interest rate in India in 2026?",
    a: "Public-sector banks (SBI, Bank of Baroda, PNB) start around 7.10–7.50% p.a. for CIBIL 800+ borrowers; private banks (HDFC, ICICI, Axis) start around 8.00–8.50%. Add 25–50 bps if your CIBIL is 725–779. Rates track the RBI repo rate (currently ~7.10%) plus the lender's spread.",
  },
  {
    q: "Is a CIBIL score of 725 good for a home loan in India?",
    a: "Yes — 725+ qualifies you for most home-loan products, but you'll pay 25–50 basis points more than borrowers with 800+ scores. Bracket: 750+ = best rate, 700–749 = small premium, 650–699 = lenders may require co-applicant or higher down payment.",
  },
  {
    q: "What is Section 24B for home loan interest deduction?",
    a: "Section 24B of the Income Tax Act allows up to ₹2 lakh per year deduction on home-loan interest paid for self-occupied property. For let-out property the deduction is uncapped against rental income. This is in addition to Section 80C which covers principal repayment (up to ₹1.5 lakh/year).",
  },
  {
    q: "What is the Rental Snowball strategy?",
    a: "Rental Snowball is a multi-property investment plan where your monthly salary surplus + portfolio cashflow accumulate in a 'Snowball Fund' that pays the down payment for the next property automatically. Estima's Snowball Planner simulates this month-by-month across 360 months, with Pre-EMI vs Full EMI, Lump-sum/CLP/Subvention plans, and a 'Sweet Spot' detector — the year your portfolio funds itself.",
  },
  {
    q: "Should I prepay my home loan or invest in mutual funds?",
    a: "Prepay if your loan rate (8.5–9.5% post-tax) is higher than your expected after-tax SIP return (typically 9–11% pre-tax = 6.5–8% post-LTCG). For most Indian borrowers in 2026, prepaying a 1–2 EMI/year on the highest-rate loan beats an additional SIP. Estima's Loan Optimizer compares both side-by-side.",
  },
  {
    q: "How is rental income from property taxed in India?",
    a: "Rental income is taxed as 'Income from House Property'. You get a standard deduction of 30% on the Net Annual Value, plus full deduction of municipal taxes paid. Net income is added to your slab. For commercial property, the tenant deducts 10% TDS. Home loan interest under Section 24B can offset rental income (uncapped for let-out property).",
  },
  {
    q: "How do I calculate XIRR for my property investment?",
    a: "XIRR is the annualised internal rate of return across irregular cash flows: down payment + EMI outflows + rental inflows + final sale value minus outstanding loan. Estima's portfolio tracker computes True XIRR automatically using Newton-Raphson iteration across all your properties combined, including post-tax adjustments.",
  },
  {
    q: "What is the stamp duty in Mumbai, Bangalore and Delhi in 2026?",
    a: "Maharashtra (Mumbai): 5% (6% for men, 5% for women + 1% registration). Karnataka (Bangalore): 5–6.6% depending on property value. Delhi: 4% for women, 6% for men + 1% registration. Estima's Stamp Duty calculator applies the correct slab and women-buyer concession automatically for all major Indian states.",
  },
  {
    q: "How can I manage multiple home, car and personal loans together?",
    a: "Estima's Loan Rebalancer puts all your loans and overdrafts in one place — add them one by one or upload up to 25 from an Excel template. You can split a bonus across loans, simulate prepayments, recalculate after an RBI rate change, track paid vs remaining with custom charts, and download PDF or Excel reports.",
  },
  {
    q: "Can AI tell me which loan to prepay first?",
    a: "Yes — Estima's AI Loan Suggestions take your loans, available money and goal (lower EMI, save interest, become debt-free sooner or stay liquid) and suggest a plan. Estima's calculator then rechecks every figure, lock-in and prepayment charge, so the numbers you see are exact. It is guidance, not regulated advice.",
  },
  {
    q: "Can I get a Will drafted online in India?",
    a: "Yes — Estima drafts your Will using AI based on your assets and beneficiaries, then routes it to a registered Indian lawyer for review and to verified witnesses for signing. The flow complies with the Indian Succession Act 1925 and Hindu Succession Act 1956. A notarisation-ready PDF and e-signing flow are included.",
  },
  {
    q: "Is an Estima-drafted Will legally valid in India?",
    a: "Yes — once the Will is reviewed by a registered Indian lawyer on the Estima panel and signed by you in the presence of two attesting witnesses (per Section 63 of the Indian Succession Act, 1925). Estima generates the notarisation-ready PDF and supports lawyer e-sign + witness verification to ensure full legal validity.",
  },
];

export default function FAQAccordion() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section
      id="faq"
      className="border-t hairline bg-[hsl(var(--background))]"
      data-testid="landing-faq-section"
    >
      <div className="max-w-4xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <div className="eyebrow text-[hsl(var(--secondary))] mb-3">
            Frequently asked
          </div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight">
            Honest answers to the questions you&apos;d ask a CA.
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Real numbers. Indian tax code. 2026 RBI rates. No glossed-over
            edge-cases.
          </p>
        </div>

        <div
          className="rounded-xl border hairline overflow-hidden bg-card"
          data-testid="landing-faq-list"
          itemScope
          itemType="https://schema.org/FAQPage"
        >
          {FAQ_ITEMS.map((item, idx) => {
            const open = idx === openIdx;
            return (
              <div
                key={item.q}
                className={`border-b hairline last:border-b-0 ${
                  open ? "bg-[hsl(var(--muted))]/40" : ""
                }`}
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : idx)}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 hover:bg-[hsl(var(--muted))]/30 transition-colors"
                  aria-expanded={open}
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span
                    itemProp="name"
                    className="font-serif text-lg leading-snug pr-4"
                  >
                    {item.q}
                  </span>
                  <CaretDown
                    size={18}
                    weight="bold"
                    className={`shrink-0 transition-transform text-muted-foreground ${
                      open ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {open && (
                  <div
                    className="px-6 pb-6 -mt-1 text-sm leading-relaxed text-muted-foreground"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                    data-testid={`faq-answer-${idx}`}
                  >
                    <p itemProp="text">{item.a}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <a
            href="/seo"
            className="text-sm text-[hsl(var(--primary))] underline-offset-4 hover:underline"
            data-testid="landing-faq-deep-dive"
          >
            Read the long-form 2026 India property guides →
          </a>
        </div>
      </div>
    </section>
  );
}
